import useMintFeedback from 'core/hooks/useMintFeedback';
import BasicList from 'core/modules/components/lists/components/BasicList/BasicList';
import QuestionItem from 'core/modules/components/lists/questions/QuestionItem/QuestionItem';
import { CONTENT_TYPES } from 'core/variables/constant';
import React from 'react';

/**
 * QuestionsList - Lista de preguntas de una evaluación. Envuelve el genérico v2
 * `BasicList` (semántica `<ul>/<li>` accesible) y renderiza un `QuestionItem` por pregunta.
 */

// `QuestionItem` es JS (props sin tipar): se aliasa a un componente permisivo para poder
// inyectar las props de dominio de cada pregunta mediante spread.
const QuestionRow = QuestionItem as unknown as React.FC<Record<string, unknown>>;

// Singleton de módulo: default estable para no crear una función nueva en cada render.
const noop = (): null => null;

export type Question = Readonly<{
  guid?: string;
  learningObjectives?: unknown;
  pointsTotal?: number;
  ranking_scale?: number;
}> & Record<string, unknown>;

type QuestionsListProps = Readonly<{
  answers?: unknown[];
  assessment?: Record<string, unknown>;
  isCorrectionMode?: boolean;
  isInstructionMode?: boolean;
  isProjectionMode?: boolean;
  isStudentResultsMode?: boolean;
  isTest?: boolean;
  job?: Record<string, unknown>;
  lang?: string;
  learningObjectivesQuestion?: unknown[];
  onOpenStudentAttachment?: (...args: unknown[]) => void;
  onScoreChange?: (...args: unknown[]) => void;
  questionDisabled?: boolean;
  questions?: Question[];
  scoreScale?: number;
  scores?: unknown[];
  showInput?: boolean;
  showSolutions?: boolean;
}>;

function QuestionsList({
  answers = [],
  assessment,
  isCorrectionMode = false,
  isInstructionMode = false,
  isProjectionMode,
  isStudentResultsMode = false,
  isTest,
  job,
  lang,
  learningObjectivesQuestion,
  onOpenStudentAttachment,
  onScoreChange = noop,
  questionDisabled,
  questions = [],
  scoreScale,
  scores = [],
  showInput,
  showSolutions,
}: QuestionsListProps): JSX.Element {
  const { isMintFeedback } = useMintFeedback({ assessment });

  return (
    <div className="questions-list">
      <BasicList
        items={questions.map((item, index) => (
          <QuestionRow
            key={item?.guid}
            {...item}
            number={index + 1}
            onScoreChange={onScoreChange}
            score={scores[index]}
            answer={answers[index]}
            showInput={showInput}
            isInstructionMode={isInstructionMode}
            isCorrectionMode={isCorrectionMode}
            isProjectionMode={isProjectionMode}
            isStudentResultsMode={isStudentResultsMode}
            showSolutions={showSolutions}
            scoreScale={scoreScale}
            onOpenStudentAttachment={onOpenStudentAttachment}
            learningObjectives={assessment?.content_type_guid === CONTENT_TYPES.QUESTION ? learningObjectivesQuestion : item?.learningObjectives}
            isMintFeedback={isMintFeedback}
            questionDisabled={questionDisabled}
            isTest={isTest}
            job={job}
            lang={lang}
            pointsTotal={item?.pointsTotal || item?.ranking_scale}
          />
        ))}
      />
    </div>
  );
}

export default QuestionsList;
